import { FC, useEffect, useMemo, useState } from 'react'
import Image from 'next/image'

import { TextLinkProps } from '../A'
import { colors } from '../../theme'
import { OnboardingStepType } from '../../types'
import ActionButton, { ActionButtonProps } from '../ActionButton'
import RadioGroup from '../FormElements/RadioGroup'
import NumberPicker from '../FormElements/NumberPicker'
import { ECollapsable } from '../Collapsable/types'

import * as SC from './styled'

export type OnboardingFormSectionProps = {
  steps: any[]
  stepsValues: any
  currentStepIndex?: number
  handleOnChange?: (name?: string, value?: string | number | boolean) => void
  handleNextStep?: any
  handleQuitOnboarding?: any
  backButtonProps?: ActionButtonProps
  nextButtonProps?: ActionButtonProps
  loginButtonProps?: ActionButtonProps
  startButtonProps?: ActionButtonProps
  quitButtonProps?: TextLinkProps
  isKeycloakProcessDone?: boolean
}

const OnboardingFormSection: FC<OnboardingFormSectionProps> = ({
  steps,
  stepsValues,
  currentStepIndex,
  handleOnChange,
  handleNextStep,
  backButtonProps,
  nextButtonProps,
  loginButtonProps,
  startButtonProps,
  quitButtonProps,
  isKeycloakProcessDone = true,
}) => {
  const [isPreloadImages, setIsPreloadImages] = useState(false)
  const currentStep = useMemo(() => {
    return steps[currentStepIndex ?? 0] ?? null
  }, [currentStepIndex, steps])
  const [isOpen, setIsOpen] = useState(false) // for collapsable

  const isCongratulations = useMemo(
    () => currentStep?.name === 'congratulations',
    [currentStep]
  )

  const forcePreloadImages = useMemo(() => {
    return isPreloadImages ? (
      <SC.PreloadImagesContainer>
        {steps.map((step, idx) => (
          <SC.StepImage key={idx} hasImage={step.image}>
            {step.image && <Image {...step.image} />}
          </SC.StepImage>
        ))}
      </SC.PreloadImagesContainer>
    ) : null
  }, [isPreloadImages, steps])

  useEffect(() => {
    setIsPreloadImages(true)
  }, [])

  useEffect(() => {
    setIsOpen(false)
  }, [currentStepIndex])

  const renderTips = useMemo(() => {
    return (
      currentStep?.tip?.content && (
        <SC.TipContainer
          $needMargin={currentStep?.type === OnboardingStepType.SLIDER}
        >
          {currentStep?.tip?.title && (
            <SC.TipTitle onClick={() => setIsOpen(!isOpen)}>
              <SC.TitleLeft>
                <SC.TipIcon icon={currentStep?.tip?.icon ?? 'question'} />
                <span>{currentStep?.tip?.title}</span>
              </SC.TitleLeft>
              <SC.TipChevron icon="chevronDown" $isOpen={isOpen} />
            </SC.TipTitle>
          )}
          <SC.TipContent className={isOpen ? 'open' : ''}>
            {currentStep?.tip?.content}
          </SC.TipContent>
        </SC.TipContainer>
      )
    )
  }, [
    currentStep?.tip?.content,
    currentStep?.tip?.icon,
    currentStep?.tip?.title,
    currentStep?.type,
    isOpen,
  ])

  const renderStep = useMemo(() => {
    const stepType = currentStep?.type ?? ''

    switch (stepType) {
      case OnboardingStepType.INTRODUCTION:
        return (
          <SC.Step>
            <SC.StepImage hasImage>
              <Image
                src="/images/onboarding/intro.png"
                width="480"
                height="342"
              />
            </SC.StepImage>
            <SC.StepHighlight
              text={currentStep?.title}
              backgroundColor={colors.greenMint}
            />
            {isKeycloakProcessDone && (
              <SC.BottomButtons>
                {loginButtonProps && <ActionButton {...loginButtonProps} />}
                {startButtonProps && <ActionButton {...startButtonProps} />}
              </SC.BottomButtons>
            )}
          </SC.Step>
        )
      case OnboardingStepType.INTERSTITIAL:
        return (
          <SC.Step>
            <SC.StepHighlight
              text={currentStep?.title}
              backgroundColor={colors.greenMint}
            />
            <SC.StepImage hasImage={currentStep.image}>
              {currentStep.image && <Image {...currentStep.image} />}
            </SC.StepImage>
            {currentStep?.texts && (
              <SC.StepTexts
                light
                className={`-intersitial ${
                  isCongratulations ? '-congratulations' : ''
                }`}
              >
                {currentStep?.texts}
              </SC.StepTexts>
            )}
            {renderTips}
            <SC.BottomButtons smallMargin>
              <ActionButton
                text={currentStep?.cta}
                variantType="button"
                variantColorScheme="black"
                actionProps={{ type: 'button', onClick: handleNextStep }}
              />
            </SC.BottomButtons>
          </SC.Step>
        )
      case OnboardingStepType.NUMBER:
        return (
          <SC.Step>
            <SC.StepHighlight
              text={currentStep?.title}
              backgroundColor={colors.greenMint}
            />
            <SC.StepImage hasImage={currentStep.image}>
              {currentStep.image && <Image {...currentStep.image} />}
            </SC.StepImage>
            {currentStep?.texts && (
              <SC.StepTexts>{currentStep?.texts}</SC.StepTexts>
            )}
            {handleOnChange && (
              <NumberPicker
                min={currentStep?.min}
                max={currentStep?.max}
                name={currentStep?.name}
                value={stepsValues?.[currentStep?.name] ?? null}
                onChange={handleOnChange}
              >
                {currentStep?.peopleCountText}
              </NumberPicker>
            )}
            {renderTips}
            <SC.BottomButtons>
              {nextButtonProps && <ActionButton {...nextButtonProps} />}
            </SC.BottomButtons>
          </SC.Step>
        )
      case OnboardingStepType.RADIO:
        return (
          <SC.Step>
            <SC.StepHighlight
              text={currentStep?.title}
              backgroundColor={colors.greenMint}
            />
            <SC.StepImage hasImage={currentStep.image}>
              {currentStep.image && <Image {...currentStep.image} />}
            </SC.StepImage>
            {currentStep?.texts && (
              <SC.StepTexts>{currentStep?.texts}</SC.StepTexts>
            )}
            {handleOnChange && (
              <RadioGroup
                items={currentStep?.options}
                name={currentStep?.name}
                value={stepsValues?.[currentStep?.name] ?? null}
                onChange={handleOnChange}
              />
            )}
            {renderTips}
            <SC.BottomButtons smallMargin>
              {nextButtonProps && <ActionButton {...nextButtonProps} />}
            </SC.BottomButtons>
          </SC.Step>
        )
      case OnboardingStepType.SLIDER:
        return (
          <SC.Step key={currentStep?.name}>
            <SC.StepHighlight
              text={currentStep?.title}
              backgroundColor={colors.greenMint}
            />
            <SC.StepImage hasImage={currentStep.image}>
              {currentStep.image && <Image {...currentStep.image} />}
            </SC.StepImage>
            {currentStep?.texts && (
              <SC.StepTexts>{currentStep?.texts}</SC.StepTexts>
            )}
            {handleOnChange && (
              <SC.OnboardingSlider
                count={currentStep?.options?.length}
                items={currentStep?.options}
                name={currentStep?.name}
                value={stepsValues?.[currentStep?.name] ?? null}
                onChange={handleOnChange}
              />
            )}
            {renderTips}
            <SC.BottomButtons smallMargin={currentStep?.tip?.content}>
              {nextButtonProps && <ActionButton {...nextButtonProps} />}
            </SC.BottomButtons>
          </SC.Step>
        )
      case OnboardingStepType.GENERATION:
        return null
      default:
        return null
    }
  }, [
    currentStep?.type,
    currentStep?.title,
    currentStep.image,
    currentStep?.texts,
    currentStep?.cta,
    currentStep?.min,
    currentStep?.max,
    currentStep?.name,
    currentStep?.peopleCountText,
    currentStep?.options,
    currentStep?.tip?.content,
    isKeycloakProcessDone,
    loginButtonProps,
    startButtonProps,
    isCongratulations,
    renderTips,
    handleNextStep,
    handleOnChange,
    stepsValues,
    nextButtonProps,
  ])

  const isQuitButton = useMemo(() => {
    return (
      isKeycloakProcessDone &&
      !isCongratulations &&
      [
        OnboardingStepType.INTRODUCTION,
        OnboardingStepType.INTERSTITIAL,
        OnboardingStepType.NUMBER,
        OnboardingStepType.RADIO,
        OnboardingStepType.SLIDER,
      ].includes(currentStep?.type ?? '')
    )
  }, [currentStep?.type, isCongratulations, isKeycloakProcessDone])

  const stepsWithStepper = useMemo(
    () => [
      OnboardingStepType.INTERSTITIAL,
      OnboardingStepType.NUMBER,
      OnboardingStepType.RADIO,
      OnboardingStepType.SLIDER,
    ],
    []
  )

  const shouldDisplayStepper = useMemo(() => {
    return stepsWithStepper.includes(currentStep?.type ?? '')
  }, [currentStep, stepsWithStepper])

  return (
    <>
      {forcePreloadImages}
      {shouldDisplayStepper && (
        <SC.OnboardingStepper
          currentStep={currentStepIndex}
          totalSteps={
            steps.filter((s) => stepsWithStepper.includes(s.type))?.length
          }
        />
      )}
      <SC.OnboardingCollapsable
        className={`${!shouldDisplayStepper ? 'isAlone' : ''}`}
        isCollapsable={ECollapsable.NONE}
        isOpenOnInit
        header={
          (currentStepIndex ?? 0) > 1 &&
          currentStep?.type !== OnboardingStepType.GENERATION &&
          backButtonProps && (
            <SC.BackContainer>
              <SC.BackButton {...backButtonProps} />
              <SC.BackButtonMobile {...backButtonProps} variantIcon="only" />
            </SC.BackContainer>
          )
        }
      >
        <SC.Inner>
          {renderStep}
          {isQuitButton && quitButtonProps && (
            <SC.QuitOnboarding {...quitButtonProps}>
              {quitButtonProps?.text}
            </SC.QuitOnboarding>
          )}
          {currentStep?.legend && <SC.Legend>{currentStep?.legend}</SC.Legend>}
        </SC.Inner>
      </SC.OnboardingCollapsable>
    </>
  )
}

export default OnboardingFormSection
